/**
 * Founder Bonus campaign card.
 *
 * Public-facing card that announces the first-50 founders gift box and shows
 * how many slots are left. Polls `/api/founder-campaign/status` on mount;
 * once `active=false` the entire component renders nothing so the campaign
 * disappears the moment the cap is hit (per launch decision).
 *
 * Two render modes:
 *   - variant="hero"    Big card placed in the home-page hero strip.
 *   - variant="inline"  Compact pill placed inside the register flow.
 */
import React, { useEffect, useState } from 'react';
import { Gift, Sparkles, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLang } from '@/lib/LangContext';

const API = process.env.REACT_APP_BACKEND_URL || '';

function useFounderStatus() {
  const [status, setStatus] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/api/founder-campaign/status`);
        if (!r.ok) throw new Error('status http ' + r.status);
        const data = await r.json();
        if (!cancelled) setStatus(data);
      } catch {
        if (!cancelled) setStatus({ active: false });
      }
    })();
    return () => { cancelled = true; };
  }, []);
  return status;
}

export default function FounderBonusCard({ variant = 'hero' }) {
  const { lang } = useLang();
  const status = useFounderStatus();
  if (!status || !status.active) return null;

  const L = (en, ka) => (lang === 'ka' ? ka : en);
  const remaining = status.remaining;

  if (variant === 'inline') {
    return (
      <div
        data-testid="founder-bonus-inline"
        className="flex items-center gap-3 rounded-2xl border-2 border-[#F25C05]/35 bg-[#F25C05]/8 px-4 py-3 text-sm"
      >
        <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#F25C05] text-white">
          <Gift className="h-4 w-4" />
        </span>
        <div className="leading-snug">
          <p className="font-bold text-[#05223D]">
            {L(
              `Founder Bonus included — only ${remaining}/${status.cap} spots left.`,
              `Founder Bonus შენთვის — დარჩა მხოლოდ ${remaining}/${status.cap} ადგილი.`,
            )}
          </p>
          <p className="text-xs text-[#465B70] mt-0.5">
            {L(
              'Complete the registration to claim your individually curated SmartPaw Gift Box.',
              'დაასრულე რეგისტრაცია და მიიღე შენი ცხოველისთვის სპეციალურად შერჩეული SmartPaw Gift Box-ი.',
            )}
          </p>
        </div>
      </div>
    );
  }

  return (
    <section
      data-testid="founder-bonus-hero"
      className="relative mx-auto max-w-7xl px-5 md:px-10 mt-6 md:mt-10"
      aria-label="Founder Bonus campaign"
    >
      <div className="relative overflow-hidden rounded-3xl border border-[#F25C05]/25 bg-gradient-to-br from-[#FFF6EE] via-white to-[#FDEBDC] p-6 md:p-9 shadow-[0_18px_50px_-25px_rgba(242,92,5,0.55)]">
        <div className="flex flex-col md:flex-row md:items-center gap-6 md:gap-10">
          {/* Sparkle icon + eyebrow */}
          <div className="flex items-center gap-3 md:flex-col md:items-start md:gap-3 md:shrink-0">
            <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-[#F25C05] text-white shadow-md">
              <Sparkles className="h-6 w-6" />
            </span>
            <span className="text-xs tracking-[0.22em] uppercase font-bold text-[#F25C05] whitespace-nowrap">
              {L('Founder Bonus', 'Founder Bonus')}
            </span>
          </div>

          {/* Main copy — single sentence, no description */}
          <h2 className="flex-1 font-display font-bold text-[#05223D] text-xl sm:text-2xl md:text-[26px] tracking-tight leading-[1.25]">
            {L(
              'Be among the first 50 registered users and receive a SmartPaw Gift Box — created especially for your four-legged friend.',
              'იყავი პირველ 50 დარეგისტრირებულ მომხმარებელს შორის და მიიღე SmartPaw სასაჩუქრე ნაკრები — შექმნილი სპეციალურად შენი ოთხფეხასთვის.',
            )}
          </h2>

          {/* Counter + CTA */}
          <div className="flex flex-row md:flex-col items-center md:items-end gap-3 md:gap-4 md:shrink-0">
            <div
              className="flex items-baseline gap-1 rounded-2xl bg-white border border-[#F25C05]/25 px-4 py-2.5 shadow-sm"
              data-testid="founder-bonus-counter"
            >
              <span className="font-display font-extrabold text-3xl md:text-4xl tracking-[-0.02em] text-[#F25C05] tabular-nums">
                {remaining}
              </span>
              <span className="font-bold text-[#465B70] text-xs md:text-sm">
                / {status.cap} {L('left', 'დარჩა')}
              </span>
            </div>
            <Link
              to="/cabinet/register"
              className="inline-flex items-center gap-2 rounded-full bg-[#F25C05] px-5 py-3 text-sm font-bold text-white shadow-md hover:bg-[#d44e02] transition-all hover:-translate-y-0.5 whitespace-nowrap"
              data-testid="founder-bonus-cta"
            >
              {L('Learn the terms', 'გაიგე პირობები')}
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
